import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const Field = ({ label, type, value, onChange, placeholder, testid, autoComplete }) => (
  <div>
    <label className="caps-tag mb-1.5 block text-white/40">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      data-testid={testid}
      className="mono h-11 w-full rounded-[8px] border border-white/10 bg-[#050508] px-3 text-sm font-light text-white placeholder:text-white/25 focus:border-[#00FF87]/50 focus:outline-none"
    />
  </div>
);

export default function Login() {
  const { login, register, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const isRegister = mode === "register";

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return toast.error("Inserisci email e password");
    if (isRegister && password.length < 6) return toast.error("La password deve avere almeno 6 caratteri");
    setBusy(true);
    try {
      if (isRegister) await register(email.trim(), password, name.trim());
      else await login(email.trim(), password);
      toast.success(isRegister ? "Account creato" : "Bentornato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Operazione non riuscita");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-dvh overflow-y-auto" data-testid="login-screen">
      <div className="flex min-h-dvh flex-col justify-center px-6 py-8">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mono flex items-center justify-center text-3xl font-light tracking-[-0.02em] text-white">
          LIQO<span className="liqo-blink ml-1 text-[#00FF87]">▌</span>
        </div>
        <p className="caps-tag mt-2 text-white/35">Il tuo CFO AI · finanza per freelancer</p>
      </div>

      <form onSubmit={submit} className="space-y-3">
        {isRegister && (
          <Field label="Nome" type="text" value={name} onChange={setName} placeholder="Come ti chiami" testid="login-name" autoComplete="name" />
        )}
        <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="tu@esempio.com" testid="login-email" autoComplete="email" />
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          placeholder={isRegister ? "almeno 6 caratteri" : "••••••••"}
          testid="login-password"
          autoComplete={isRegister ? "new-password" : "current-password"}
        />

        <button
          type="submit"
          disabled={busy}
          data-testid="login-submit"
          className="mono mt-1 flex h-11 w-full items-center justify-center gap-2 rounded-[8px] border border-[#00FF87]/50 bg-[#00FF87]/[0.06] text-[12px] font-medium uppercase tracking-[0.16em] text-[#00FF87] shadow-[0_0_24px_rgba(0,255,135,0.12)] active:scale-[0.98] disabled:opacity-50"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          {isRegister ? "Crea account" : "Accedi"}
        </button>
      </form>

      <div className="my-4 flex items-center gap-3">
        <div className="h-px flex-1 bg-white/[0.08]" />
        <span className="caps-tag text-white/25">oppure</span>
        <div className="h-px flex-1 bg-white/[0.08]" />
      </div>

      <button
        onClick={loginWithGoogle}
        data-testid="login-google"
        className="flex h-11 w-full items-center justify-center gap-2.5 rounded-[8px] border border-white/12 bg-[#0D0D14] text-[13px] font-light text-white/85 hover:bg-white/[0.04] active:scale-[0.98]"
      >
        <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
          <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 5.1 29.4 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21c10.5 0 20-7.6 20-21 0-1.3-.1-2.3-.4-3.5z"/>
          <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 5.1 29.4 3 24 3 16 3 9.1 7.6 6.3 14.7z"/>
          <path fill="#4CAF50" d="M24 45c5.2 0 10-2 13.6-5.2l-6.3-5.2C29.2 36 26.7 37 24 37c-5.3 0-9.7-2.6-11.3-7l-6.5 5C9 40.3 15.9 45 24 45z"/>
          <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.6l6.3 5.2C41.9 36.8 44 31 44 24c0-1.3-.1-2.3-.4-3.5z"/>
        </svg>
        Continua con Google
      </button>

      <button
        onClick={() => setMode(isRegister ? "login" : "register")}
        data-testid="login-toggle-mode"
        className="mono mt-5 text-center text-[12px] text-white/45 hover:text-white/70"
      >
        {isRegister ? "Hai già un account? Accedi" : "Non hai un account? Registrati"}
      </button>

      <p className="mt-6 text-center text-[11px] leading-relaxed text-white/30">
        Continuando accetti i{" "}
        <Link to="/terms" className="text-white/55 underline underline-offset-2">Termini</Link> e la{" "}
        <Link to="/privacy" className="text-white/55 underline underline-offset-2">Privacy</Link>.
      </p>
      </div>
    </div>
  );
}
